import { NgClass, NgStyle } from '@angular/common';
import { Component } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MessageService } from 'primeng/api';
import { Checkbox } from 'primeng/checkbox';
import { Paginator, PaginatorState } from 'primeng/paginator';
import { Tooltip } from 'primeng/tooltip';
import Notification from '../../../../../types/notifcation/Notification';
import BatteryStatus from '../../../../../types/physical-sample/BatteryStatus';
import PhysicalSample from '../../../../../types/physical-sample/PhysicalSample';
import PhysicalSampleState from '../../../../../types/physical-sample/PhysicalSampleState';
import Sample from '../../../../../types/sample/Sample';
import { SampleState } from '../../../../../types/sample/SampleState';
import { EnumLabelPipe } from '../../../pipes/enum-label.pipe';
import { RetailerService } from '../../../services/retailer.service';
import { SampleCategoryService } from '../../../services/sample-category.service';
import { SampleTypeService } from '../../../services/sample-type.service';
import { SampleService } from '../../../services/sample.service';
import { RetailerTopBarComponent } from '../retailer-top-bar/retailer-top-bar.component';

@Component({
    selector: 'app-retailer-manage-samples',
    imports: [
        RetailerTopBarComponent,
        ReactiveFormsModule,
        FormsModule,
        NgClass,
        Checkbox,
        Paginator,
        NgStyle,
        Tooltip,
        EnumLabelPipe
    ],
    templateUrl: './retailer-manage-samples.component.html',
    styleUrl: './retailer-manage-samples.component.scss'
})
export class RetailerManageSamplesComponent {
    public search: string = '';

    public pageSize: number = 10;
    public page: number = 0;
    public totalRecords: number = 0;

    public samples: PhysicalSample[] = [];
    public sampleCatalog: Sample[] = [];

    public unreadNotificationCount: number = 0;
    public notifications: Notification[] = [];
    public showNotificationWindow: boolean = false;
    public showAllNotifications: boolean = false;
    public loadingNotifications: boolean = false;
    public filtersApplied: boolean = false;

    public tabs: string[] = [
        'My Samples',
        'Sample Catalog'
    ];
    public selectedTab: string = 'My Samples';
    public filtersExpanded: boolean = false;
    public expirationFilterExpanded: boolean = false;
    public sampleTypeFilterExpanded: boolean = false;
    public sampleCategoryFilterExpanded: boolean = false;
    public sampleStateFilterExpanded: boolean = false;
    public batteryStateFilterExpanded: boolean = false;
    public drawerStateFilterExpanded: boolean = false;

    public selectedSampleIds: Set<number> = new Set<number>();

    public expirationFilters: FilterItem[] = [
        { id: 1, name: 'Active', selected: false },
        { id: 2, name: 'Expired', selected: false },
        { id: 3, name: 'Expiring in a Month', selected: false },
        { id: 4, name: 'Expiring in a Year', selected: false },
    ];

    public sampleStateFilters: FilterItem[] = [
        { id: 1, name: 'Online', selected: false },
        { id: 2, name: 'Offline', selected: false },
        { id: 3, name: 'Unknown', selected: false },
    ];

    public batteryStateFilters: FilterItem[] = [
        { id: 1, name: 'Full', selected: false },
        { id: 2, name: 'High', selected: false },
        { id: 3, name: 'Medium', selected: false },
        { id: 4, name: 'Low', selected: false },
        { id: 5, name: 'Critical', selected: false },
        { id: 6, name: 'Charging', selected: false },
        { id: 7, name: 'Unknown', selected: false },
    ];

    public drawerStateFilters: FilterItem[] = [
        { id: 1, name: 'In Drawer', selected: false },
        { id: 2, name: 'Outside Drawer', selected: false },
    ];

    public sampleTypeFilters: FilterItem[] = [];

    public sampleCategoryFilters: FilterItem[] = [];

    constructor(private sampleTypeService: SampleTypeService, private sampleCategoryService: SampleCategoryService,
                private retailerService: RetailerService, private sampleService: SampleService,
                private messageService: MessageService) {
        this.loadSampleTypeFilters();
        this.loadSampleCategoryFilters();
        this.fetchUnreadNotificationCount();
        this.onFilterChange();
    }

    /**
     * Gets the list of displayed notifications.
     */
    public get displayedNotifications(): Notification[] {
        if (this.showAllNotifications) {
            return this.notifications;
        }

        // Only show unread notifications by default
        return this.notifications.filter(n => !n.read);
    }

    /**
     * Toggles selection of a sample.
     * @param sample The clicked sample
     */
    public onSelected(sample: PhysicalSample): void {
        const id = sample.id;
        if (this.selectedSampleIds.has(id)) {
            this.selectedSampleIds.delete(id);
        } else {
            this.selectedSampleIds.add(id);
        }

        if (this.selectedSampleIds.size !== 0) {
            this.retailerService.turnLightsOn(Array.from(this.selectedSampleIds)).catch((error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to turn lights on: ' + error.message,
                    life: 3000
                });
            });
        } else {
            this.retailerService.turnLightsOff().catch((error) => {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Failed to turn lights off: ' + error.message,
                    life: 3000
                });
            });
        }
    }

    /**
     * Checks if a sample is selected.
     * @param  sample The sample to check
     * @return True if the sample is selected, false otherwise.
     */
    public isSelected(sample: PhysicalSample): boolean {
        return this.selectedSampleIds.has(sample.id);
    }

    /**
     * Handles the filter input change event.
     */
    public onFilterChange(): void {
        this.page = 0;

        this.filtersApplied =
            this.expirationFilters.some(f => f.selected) ||
            this.sampleTypeFilters.some(f => f.selected) ||
            this.sampleCategoryFilters.some(f => f.selected) ||
            this.batteryStateFilters.some(f => f.selected) ||
            this.sampleStateFilters.some(f => f.selected) ||
            this.drawerStateFilters.some(f => f.selected);
        if (this.selectedTab === 'My Samples') {
            this.fetchSamples();
        } else if (this.selectedTab === 'Sample Catalog') {
            this.fetchSampleCatalog();
        }
    }

    /**
     * Toggle the notification window visibility.
     *
     * @param show True to show, false to hide.
     */
    public toggleNotificationWindow(show: boolean): void {
        this.fetchNotifications();
        this.showNotificationWindow = show;
    }

    /**
     * Marks a notification as read by its ID.
     */
    public markNotificationAsRead(id: number): void {
        this.retailerService.markNotificationAsRead(id).then(() => {
                this.fetchNotifications();
                this.fetchUnreadNotificationCount();

                this.messageService.add({
                    severity: 'success',
                    summary: 'Notification Read',
                    detail: 'Notification marked as read successfully.',
                    life: 3000
                });
            }
        ).catch((error) => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to mark notification as read: ' + error.message,
                life: 3000
            });
        });

    }

    /**
     * Marks all notifications as read.
     */
    public markAllNotificationsAsRead(): void {
        this.retailerService.markAllNotificationsAsRead().then(() => {
            this.fetchNotifications();
            this.fetchUnreadNotificationCount();
            this.messageService.add({
                severity: 'success',
                summary: 'All Notifications Read',
                detail: 'All notifications marked as read successfully.',
                life: 3000
            });
        }).catch((error) => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to mark all notifications as read: ' + error.message,
                life: 3000
            });
        });
    }

    /**
     * Handles the tab change event.
     *
     * @param tab The name of the selected tab.
     */
    public onTabChange(tab: string): void {
        this.selectedTab = tab;
        this.clearFilters();
    }

    /**
     * Clears all filters and resets the search input.
     */
    public clearFilters(): void {
        this.search = '';
        this.expirationFilters.forEach((filter) => filter.selected = false);
        this.sampleTypeFilters.forEach((filter) => filter.selected = false);
        this.sampleCategoryFilters.forEach((filter) => filter.selected = false);
        this.page = 0; // Reset to first page on tab change
        this.filtersApplied = false;
        if (this.selectedTab === 'My Samples') {
            this.fetchSamples();
        } else if (this.selectedTab === 'Sample Catalog') {
            this.fetchSampleCatalog();
        }
    }

    /**
     * Returns an array of selected sample type IDs.
     *
     * @return An array of selected sample type IDs.
     */
    public getSelectedTypeIds(): number[] {
        return this.sampleTypeFilters
            .filter((f) => f.selected)
            .map((f) => f.id);
    }

    /**
     * Returns an array of selected sample category IDs.
     *
     * @return An array of selected sample category IDs.
     */
    public getSelectedCategoryIds(): number[] {
        return this.sampleCategoryFilters
            .filter((f) => f.selected)
            .map((f) => f.id);
    }

    /**
     * Transforms a date string (YYYY-MM-DD) into a formatted date string (DD.MM.YYYY).
     *
     * @param date The date string in "YYYY-MM-DD" format.
     * @return The formatted date string in "DD.MM.YYYY" format, or an empty string if the input is invalid.
     */
    public transformDate(date: string): string {
        if (!date) {
            return '';
        }
        const parts = date.split('-');
        if (parts.length !== 3) {
            return date; // Return original date if format is unexpected
        }
        return `${ parts[2] }.${ parts[1] }.${ parts[0] }`; // Return formatted date as "DD.MM.YYYY"
    }

    /**
     * Handles the page change event from the paginator.
     * @param $event The paginator state event.
     */
    public onPageChange($event: PaginatorState) {
        this.page = $event.page || 0;
        this.pageSize = $event.rows || 10;
        if (this.selectedTab === 'My Samples') {
            this.fetchSamples();
        } else if (this.selectedTab === 'Sample Catalog') {
            this.fetchSampleCatalog();
        }
    }

    /**
     * Fetches the samples from the RetailerService with applied filters.
     */
    private fetchSamples(): void {
        this.retailerService
            .getSamples(
                this.search, this.isActiveFilterSelected(), this.isExpiredFilterSelected(),
                this.getExpirationDateBefore(),
                this.getSelectedTypeIds(), this.getSelectedCategoryIds(), this.getSelectedSampleStates(),
                this.getSelectedBatteryStates(), this.getIsInDrawerFilter(), this.page, this.pageSize
            ).then((samplesPage) => {
            this.samples = samplesPage.content.map((sample) => {
                    let ps = new PhysicalSample(sample);
                    if (ps.sample) {
                        ps.sample.detailNames = this.getDetailNames(ps?.sample);
                    }
                    return ps;
                }
            );
            this.page = samplesPage.page.number;
            this.pageSize = samplesPage.page.size;
            this.totalRecords = samplesPage.page.totalElements;
        }).catch((error) => {
            console.error('Error fetching samples:', error);
        });
    }

    /**
     * Fetches the sample catalog from the SampleService with applied filters.
     */
    private fetchSampleCatalog(): void {
        this.sampleService.getSamples(
            this.search, this.page, this.pageSize, undefined, undefined,
            this.getSelectedTypeIds().length !== 0 ? this.getSelectedTypeIds() : undefined,
            this.getStateFilter()?.map(state => state.toString()),
            this.getSelectedCategoryIds().length !== 0 ? this.getSelectedCategoryIds() : undefined, undefined,
            undefined,
            this.getExpirationDateBefore(), undefined, undefined
        ).then((samplePage) => {
            this.sampleCatalog = samplePage.content;
            this.sampleCatalog = this.sampleCatalog.map((sample) => {
                sample.detailNames = this.getDetailNames(sample);
                return sample;
            });
            this.page = samplePage.page.number;
            this.pageSize = samplePage.page.size;
            this.totalRecords = samplePage.page.totalElements;
        }).catch((error) => {
            console.error('Error fetching sample catalog:', error);
        });
    }

    /**
     * Loads sample types from the SampleTypeService and initializes the sampleTypeFilters array.
     */
    private loadSampleTypeFilters(): void {
        this.sampleTypeService
            .getAllSampleTypes()
            .then((sampleTypes) => {
                this.sampleTypeFilters = sampleTypes.map((st) => ( {
                    id: st.id,
                    name: st.name,
                    selected: false,
                } ));
            })
            .catch((error) => {
                console.error('Error loading sample types:', error);
            });
    }

    /**
     * Loads sample categories from the SampleCategoryService and initializes the sampleCategoryFilters array.
     */
    private loadSampleCategoryFilters(): void {
        this.sampleCategoryService
            .getAllSampleCategories()
            .then((sampleCategories) => {
                this.sampleCategoryFilters = sampleCategories.map((cat) => ( {
                    id: cat.id,
                    name: cat.name,
                    selected: false,
                } ));
            })
            .catch((error) => {
                console.error('Error loading sample categories:', error);
            });
    }

    /**
     * Fetches the count of unread notifications from the retailer service.
     */
    private fetchUnreadNotificationCount(): void {
        this.retailerService.getUnreadNotificationCount().then((count) => {
            this.unreadNotificationCount = count;
        }).catch((error) => {
            console.error('Error fetching unread notification count:', error);
        });
    }

    /**
     * Fetches notifications from the retailer service.
     */
    private fetchNotifications(): void {
        this.loadingNotifications = true;
        this.retailerService.getNotifications().then((notifications) => {
            this.notifications = notifications;
        }).catch((error) => {
            console.error('Error fetching notifications:', error);
        }).finally(() => {
            this.loadingNotifications = false;
        });
    }

    /**
     * Returns the expiration date before which samples should be filtered.
     * If 'Active' is selected, it returns undefined.
     * If 'Expiring in a Year' is selected, it returns a date one year from today.
     * If 'Expiring in a Month' is selected, it returns a date one month from today.
     * If no specific expiration filter is selected, it returns undefined.
     *
     * @return The expiration date in "yyyy-MM-dd" format or undefined if 'Active' is selected.
     */
    private getExpirationDateBefore(): string | undefined {
        const activeFilter = this.expirationFilters.find((f) => f.selected && f.name === 'Active');
        if (activeFilter) {
            return undefined;
        }
        const expiringInYearFilter = this.expirationFilters.find((f) => f.selected && f.name === 'Expiring in a Year');
        if (expiringInYearFilter) {
            const date = new Date();
            date.setFullYear(date.getFullYear() + 1);
            return date.toISOString().split('T')[0]; // Return date in "yyyy-MM-dd" format
        }
        const expiringInMonthFilter = this.expirationFilters.find(
            (f) => f.selected && f.name === 'Expiring in a Month');
        if (expiringInMonthFilter) {
            const date = new Date();
            date.setMonth(date.getMonth() + 1);
            return date.toISOString().split('T')[0]; // Return date in "yyyy-MM-dd" format
        }
        return undefined; // No specific expiration date filter selected
    }

    /**
     * Checks if the 'Active' filter is selected.
     * @return True if the 'Active' filter is selected, false otherwise.
     * @private
     */
    private isActiveFilterSelected(): boolean {
        return this.expirationFilters.some((f) => f.selected && f.name === 'Active');
    }

    /**
     * Checks if the 'Expired' filter is selected.
     * @return True if the 'Expired' filter is selected, false otherwise.
     * @private
     */
    private isExpiredFilterSelected(): boolean {
        return this.expirationFilters.some((f) => f.selected && f.name === 'Expired');
    }

    /**
     * Determines the sample state filter based on the selected expiration filters.
     * - If both 'Active' and 'Expired' are selected, or neither is selected, returns both states.
     * - If only 'Active' is selected, returns [SampleState.ACTIVE].
     * - If only 'Expired' is selected, returns [SampleState.ARCHIVED].
     * - If neither is selected, returns undefined.
     * @return An array of SampleState or undefined.
     */
    private getStateFilter(): SampleState[] | undefined {
        if (this.isActiveFilterSelected() && this.isExpiredFilterSelected() ||
            ( !this.isActiveFilterSelected() && !this.isExpiredFilterSelected() )) {
            return [
                SampleState.ACTIVE,
                SampleState.ARCHIVED
            ];
        }
        if (this.isActiveFilterSelected()) {
            return [ SampleState.ACTIVE ];
        }
        if (this.isExpiredFilterSelected()) {
            return [ SampleState.ARCHIVED ];
        }
        return undefined;
    }

    /**
     * Gets the selected sample states from the sampleStateFilters.
     *
     * @return  An array of selected PhysicalSampleState values.
     */
    private getSelectedSampleStates(): PhysicalSampleState[] {
        return this.sampleStateFilters
            .filter(f => f.selected)
            .map(f => f.name.toUpperCase() as PhysicalSampleState);
    }

    /**
     * Gets the selected battery states from the batteryStateFilters.
     *
     * @return An array of selected BatteryStatus values.
     */
    private getSelectedBatteryStates(): BatteryStatus[] {
        return this.batteryStateFilters
            .filter(f => f.selected)
            .map(f => f.name.toUpperCase() as BatteryStatus);
    }

    /**
     * Gets the selected drawer state from the drawerStateFilters.
     *
     * @return
     * @private
     */
    private getIsInDrawerFilter(): boolean | undefined {
        const inDrawerFilter = this.drawerStateFilters.find(f => f.selected && f.name === 'In Drawer');
        const outsideDrawerFilter = this.drawerStateFilters.find(f => f.selected && f.name === 'Outside Drawer');

        if (inDrawerFilter && !outsideDrawerFilter) {
            return true;
        }
        if (!inDrawerFilter && outsideDrawerFilter) {
            return false;
        }
        return undefined;
    }

    /**
     * Handles the event when a new studio is changed.
     */
    public onNewStudio() {
        this.onFilterChange();
        this.fetchUnreadNotificationCount();
    }

    /**
     * Gets a comma-separated string of detail names for a sample.
     * @param  sample The sample object.
     * @return A comma-separated string of detail names.
     */
    private getDetailNames(sample: Sample): string {
        return sample.categoryDetails.map(detail => detail.name).join(', ');
    }

    /**
     * Turns off the lights for all selected samples and clears the selection.
     */
    public turnLightsOff() {
        this.selectedSampleIds.clear();
        this.retailerService.turnLightsOff().catch((error) => {
            this.messageService.add({
                severity: 'error',
                summary: 'Error',
                detail: 'Failed to turn lights off: ' + error.message,
                life: 3000
            });
        });
    }
}

export interface FilterItem {
    id: number;
    name: string;
    selected: boolean;
}


